/* eslint-disable max-lines */
import { Nullable, Project, ProjectDetails, Option } from '@customTypes';
import {
  dialogDefaultState,
  PROJECT_ROLES,
  PROJECT_TABS,
  SORT_TYPES,
  STEP,
  ZERO,
  translationDialogDefaultState,
} from '@constants';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  ProjectCreation,
  ProjectSetup,
  SegmentationCategoryDiaologState,
  QuestionCategoryDialogState,
  SelectedProject,
  StaffCreationDialogState,
  StaffTransferDialogState,
  ProjectSetupFormState,
  DeleteCompetitorDiaologState,
  SetupLanguageDialog,
  AttributeCategoryDialogState,
  SortProjectState,
} from '@store/types';

export interface ProjectState {
  projects: Project[];
  selectedProject: Nullable<SelectedProject>;
  sortProjectState: SortProjectState;
  projectCreationDialog: ProjectCreation;
  projectSetup: ProjectSetup;
  projectDetails: Nullable<ProjectDetails>;
  projectSetupFormState: ProjectSetupFormState;
  staffTransferDialog: StaffTransferDialogState;
  staffCreationDialog: StaffCreationDialogState;
  isSegmentCreationDialogOpen: boolean;
  deleteSegmentCategoryIndex: Nullable<number>;
  segmentationCategoryDialog: SegmentationCategoryDiaologState;
  questionCategoryDialog: QuestionCategoryDialogState;
  isCreateCompanyDialogOpen: boolean;
  isSetupExitDialogOpen: boolean;
  isNewInterviewDialogOpen: boolean;
  isConfirmationDialogOpen: boolean;
  isConfirmationPreviewDialogOpen: boolean;
  projectAnalysisActiveTab: number;
  aggregationId: Nullable<number>;
  deleteCompetitorDialog: DeleteCompetitorDiaologState;
  userRole: PROJECT_ROLES;
  setupLanguageDialog: SetupLanguageDialog;
  isReadOnlyMode: boolean;
  currentProjectTab: PROJECT_TABS;
  editAttributeCategoryDialog: AttributeCategoryDialogState;
  languages: Option[];
  selectedCompany: Nullable<number>;
  isProjectSetupCompleted: boolean;
}

const initialState: ProjectState = {
  projects: [],
  selectedProject: null,
  sortProjectState: {
    type: SORT_TYPES.DATE,
    isAsc: null,
  },
  projectCreationDialog: dialogDefaultState,
  projectSetup: {
    activeStep: ZERO,
    savedSteps: [],
  },
  projectDetails: null,
  projectSetupFormState: {
    isDirty: false,
    isValid: true,
    dirtyForms: [],
  },
  staffTransferDialog: dialogDefaultState,
  staffCreationDialog: dialogDefaultState,
  isSegmentCreationDialogOpen: false,
  deleteSegmentCategoryIndex: null,
  segmentationCategoryDialog: {
    isOpen: false,
    isEditMode: false,
  },
  questionCategoryDialog: dialogDefaultState,
  isCreateCompanyDialogOpen: false,
  isSetupExitDialogOpen: false,
  isNewInterviewDialogOpen: false,
  isConfirmationDialogOpen: false,
  isConfirmationPreviewDialogOpen: false,
  projectAnalysisActiveTab: ZERO,
  aggregationId: null,
  deleteCompetitorDialog: dialogDefaultState,
  userRole: PROJECT_ROLES.UNDEFINED,
  setupLanguageDialog: translationDialogDefaultState,
  isReadOnlyMode: false,
  currentProjectTab: PROJECT_TABS.PROJECT_SETUP,
  editAttributeCategoryDialog: dialogDefaultState,
  languages: [],
  selectedCompany: null,
  isProjectSetupCompleted: false,
};

export const projectSlice = createSlice({
  name: 'project',
  initialState,
  reducers: {
    setFilteredProjects: (state, action: PayloadAction<Project[]>) => {
      state.projects = action.payload;
    },
    setSelectedProject: (state, action: PayloadAction<SelectedProject>) => {
      state.selectedProject = action.payload;
    },
    setSortType: (state, action: PayloadAction<SortProjectState>) => {
      state.sortProjectState = {
        type: action.payload.type,
        isAsc: !state.sortProjectState.isAsc,
      };
    },
    setProjectCreationDialog: (state, action: PayloadAction<ProjectCreation>) => {
      state.projectCreationDialog = action.payload;
    },
    handleProjectSetupNextStep: (state) => {
      state.projectSetup = {
        savedSteps: [state.projectSetup.activeStep, ...state.projectSetup.savedSteps],
        activeStep: state.projectSetup.activeStep + STEP,
      };
    },
    setProjectSetupActiveTab: (state, action: PayloadAction<number>) => {
      state.projectSetup.activeStep = action.payload;
    },
    resetProjectSetupSteps: (state) => {
      state.projectSetup = initialState.projectSetup;
    },
    setProjectDetails: (state, action: PayloadAction<ProjectDetails>) => {
      state.projectDetails = action.payload;
    },
    setProjectSetupFormState: (state, action: PayloadAction<ProjectSetupFormState>) => {
      state.projectSetupFormState = action.payload;
    },
    setStaffTransferDialog: (state, action: PayloadAction<StaffTransferDialogState>) => {
      state.staffTransferDialog = action.payload;
    },
    setStaffCreationDialog: (state, action: PayloadAction<StaffCreationDialogState>) => {
      state.staffCreationDialog = action.payload;
    },
    setSegmentCreationDiolog: (state, action: PayloadAction<boolean>) => {
      state.isSegmentCreationDialogOpen = action.payload;
    },
    setDeleteSegmentCategoryIndex: (state, action: PayloadAction<Nullable<number>>) => {
      state.deleteSegmentCategoryIndex = action.payload;
    },
    setSegmentationCategoryDialog: (state, action: PayloadAction<SegmentationCategoryDiaologState>) => {
      state.segmentationCategoryDialog = action.payload;
    },
    setQuestionCategoryDialog: (state, action: PayloadAction<QuestionCategoryDialogState>) => {
      state.questionCategoryDialog = action.payload;
    },
    setCreateCompanyDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isCreateCompanyDialogOpen = action.payload;
    },
    setSetupExitDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isSetupExitDialogOpen = action.payload;
    },
    resetFormState: (state) => {
      state.projectSetupFormState = initialState.projectSetupFormState;
    },
    setSavedFormSteps: (state, action: PayloadAction<number[]>) => {
      state.projectSetup.savedSteps = action.payload;
    },
    setNewInterviewDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isNewInterviewDialogOpen = action.payload;
    },
    setConfirmationDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isConfirmationDialogOpen = action.payload;
    },
    setConfirmationPreviewDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.isConfirmationPreviewDialogOpen = action.payload;
    },
    setDeleteCompetitorDialog: (state, action: PayloadAction<DeleteCompetitorDiaologState>) => {
      state.deleteCompetitorDialog = action.payload;
    },
    resetProjectState: (state) => {
      state.selectedProject = initialState.selectedProject;
      state.projects = initialState.projects;
      state.projectSetup = initialState.projectSetup;
      state.projectDetails = initialState.projectDetails;
      state.projectSetupFormState = initialState.projectSetupFormState;
      state.staffTransferDialog = initialState.staffTransferDialog;
      state.staffCreationDialog = initialState.staffCreationDialog;
      state.isSegmentCreationDialogOpen = initialState.isSegmentCreationDialogOpen;
      state.deleteSegmentCategoryIndex = initialState.deleteSegmentCategoryIndex;
      state.segmentationCategoryDialog = initialState.segmentationCategoryDialog;
      state.questionCategoryDialog = initialState.questionCategoryDialog;
      state.isNewInterviewDialogOpen = initialState.isNewInterviewDialogOpen;
      state.isConfirmationPreviewDialogOpen = initialState.isConfirmationPreviewDialogOpen;
      state.isConfirmationDialogOpen = initialState.isConfirmationDialogOpen;
      state.isSetupExitDialogOpen = initialState.isSetupExitDialogOpen;
      state.isCreateCompanyDialogOpen = initialState.isCreateCompanyDialogOpen;
      state.deleteCompetitorDialog = initialState.deleteCompetitorDialog;
    },
    resetProjectModalsState: (state) => {
      state.staffTransferDialog = initialState.staffTransferDialog;
      state.staffCreationDialog = initialState.staffCreationDialog;
      state.isSegmentCreationDialogOpen = initialState.isSegmentCreationDialogOpen;
      state.segmentationCategoryDialog = initialState.segmentationCategoryDialog;
      state.questionCategoryDialog = initialState.questionCategoryDialog;
      state.isNewInterviewDialogOpen = initialState.isNewInterviewDialogOpen;
      state.isConfirmationPreviewDialogOpen = initialState.isConfirmationPreviewDialogOpen;
      state.isConfirmationDialogOpen = initialState.isConfirmationDialogOpen;
      state.isNewInterviewDialogOpen = initialState.isNewInterviewDialogOpen;
      state.isSetupExitDialogOpen = initialState.isSetupExitDialogOpen;
      state.isCreateCompanyDialogOpen = initialState.isCreateCompanyDialogOpen;
      state.deleteCompetitorDialog = initialState.deleteCompetitorDialog;
    },
    setProjectAnalysisActiveTab: (state, action: PayloadAction<number>) => {
      state.projectAnalysisActiveTab = action.payload;
    },
    setAggregationId: (state, action: PayloadAction<number>) => {
      state.aggregationId = action.payload;
    },
    setUserRole: (state, action: PayloadAction<PROJECT_ROLES>) => {
      state.userRole = action.payload;
    },
    setSetupLanguageDialog: (state, action: PayloadAction<SetupLanguageDialog>) => {
      state.setupLanguageDialog = action.payload;
    },
    setSetupLanguageChanges: (state, action: PayloadAction<Record<string, { oldValue: string; newValue: string }>>) => {
      state.setupLanguageDialog.changedValues = action.payload;
    },
    setReadOnlyMode: (state, action: PayloadAction<boolean>) => {
      state.isReadOnlyMode = action.payload;
    },
    setCurrentProjectTab: (state, action: PayloadAction<PROJECT_TABS>) => {
      state.currentProjectTab = action.payload;
    },
    setEditAttributeCategoryDialog: (state, action: PayloadAction<AttributeCategoryDialogState>) => {
      state.editAttributeCategoryDialog = action.payload;
    },
    setLanguages: (state, action: PayloadAction<Option[]>) => {
      state.languages = action.payload;
    },
    setSelectedCompany: (state, action: PayloadAction<number>) => {
      state.selectedCompany = action.payload;
    },
    setProjectSetupCompleted: (state, action: PayloadAction<boolean>) => {
      state.isProjectSetupCompleted = action.payload;
    },
  },
});

export const {
  setFilteredProjects,
  setSelectedProject,
  setSortType,
  setProjectCreationDialog,
  setProjectSetupActiveTab,
  handleProjectSetupNextStep,
  setProjectDetails,
  setProjectSetupFormState,
  setStaffTransferDialog,
  setStaffCreationDialog,
  setSegmentCreationDiolog,
  setDeleteSegmentCategoryIndex,
  setSegmentationCategoryDialog,
  setQuestionCategoryDialog,
  setCreateCompanyDialogOpen,
  setSetupExitDialogOpen,
  resetFormState,
  setSavedFormSteps,
  resetProjectState,
  setNewInterviewDialogOpen,
  setConfirmationDialogOpen,
  setConfirmationPreviewDialogOpen,
  resetProjectModalsState,
  setProjectAnalysisActiveTab,
  setAggregationId,
  setDeleteCompetitorDialog,
  resetProjectSetupSteps,
  setUserRole,
  setSetupLanguageDialog,
  setSetupLanguageChanges,
  setReadOnlyMode,
  setCurrentProjectTab,
  setEditAttributeCategoryDialog,
  setLanguages,
  setSelectedCompany,
  setProjectSetupCompleted,
} = projectSlice.actions;
export default projectSlice.reducer;
